import { Component, ElementRef, AfterViewInit, OnInit, ViewChild, ChangeDetectorRef, Inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { lmwBaseRoutes } from "../../routing/route-locales/lmw-base-routes";
import { EnquiryService } from "../../services/enquiry.service";
import { ReportAnIssueCms } from '../../interfaces/report-an-issue-cms.interface';
import { freeTextValidator, fullNameValidator } from '@nx/mono-common';
import * as SignInValidators from '../../../auth-process/sign-in/validators/sign-in.validators';
import {
  CountryCode,
  parsePhoneNumber
} from 'libphonenumber-js';
import { lmwAuthLazyLoadedState } from '../../../auth-process/common/routing/route-locales/app-routes';
import { signInLazyLoaded } from '../../../auth-process/sign-in/routing/route-locales/sign-in-routes';
import { signInUsernameState } from '../../../auth-process/sign-in/routing/sign-in.states';
import { countryCodeISO } from '../../../auth-process/providers/country-code';
import { UserClaims } from '@nx/auth';



@Component({
  selector: 'lmw-base-report-an-issue',
  templateUrl: './report-an-issue.component.html'
})
export class ReportAnIssueComponent implements OnInit, AfterViewInit {
  @ViewChild('form') currentForm: ElementRef;
  @ViewChild('helpPopupRef') helpPopupRef: ElementRef;
  @ViewChild('response') submissionResponse: ElementRef;

  public enquiryForm: FormGroup;
  public reportAnIssueCmsData: ReportAnIssueCms;
  public mobileBackButtonText = lmwBaseRoutes.homeLinkText;
  public userSignedIn: boolean;
  public userRole: string;
  public showLoader = false;
  public enquirySent = false;
  public successfulResponse = false;
  public browseAllSubjectsLink: string = '/' + lmwBaseRoutes.browseSubjectsStatePath;
  public helpPopupOpen = false;
  public signInUsernamePath = `/${lmwAuthLazyLoadedState.path}/${signInLazyLoaded.path}/${signInUsernameState.path}`;
  public formHeight: number;

  private responseFragment = 'submissionResponse';
  private userClaims: UserClaims;
  private userUsesEmail: boolean;
 

  constructor(@Inject(countryCodeISO) private countryCodeISO: CountryCode, private router: Router, private route: ActivatedRoute, private formBuilder: FormBuilder, private enquiryService: EnquiryService, private cdRef:ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.reportAnIssueCmsData = this.route.snapshot.data.cmsData;
    this.userSignedIn = this.route.snapshot.data.userSignedIn;
    this.getUserClaims();
    this.createForm();
  }

  ngAfterViewInit(): void {
    this.formHeight = this.currentForm.nativeElement.offsetHeight;
    this.cdRef.detectChanges();
  }

  private getUserClaims() {
    if(this.userSignedIn) {
      this.userClaims = this.route.snapshot.data.userClaims;
      this.userUsesEmail = this.userClaims.user.attributes.email ? true : false;
      this.userRole = this.userClaims.user.apps.lmw.whyUsing;
    }
  }

  private createForm() {
    this.enquiryForm = this.formBuilder.group({
      fullName: ['', [
        Validators.required,
        fullNameValidator,
        Validators.minLength(2),
        Validators.maxLength(200)
      ]],
      emailOrMobile:['', [
        Validators.required,
        SignInValidators.emailPhoneNumberValidator(this.countryCodeISO)
      ]],
      organisationId:['', []],
      enquiry: ['', [
        Validators.required,
        Validators.minLength(2),
        freeTextValidator
      ]]
    });
    if (this.userSignedIn) {
      this.addFullNameAndContactMethodValuesAndDisableInputs();
    }
    if (this.userRole === 'supportLearnersAtCentre') {
      this.addValidatorsToOrganisationIdField();
    }
  }

  private addValidatorsToOrganisationIdField() {
    this.enquiryForm.controls.organisationId.addValidators([
      Validators.min(100), 
      Validators.max(8009999)
    ]);
  }

  private addFullNameAndContactMethodValuesAndDisableInputs() {
    this.enquiryForm.patchValue({
      fullName: this.userClaims.user.apps.lmw.fullName,
      emailOrMobile: this.userUsesEmail ? this.userClaims.user.attributes.email : this.removeCountryCodeFromNumber(this.userClaims.user.attributes.phone_number)
    });
    this.enquiryForm.controls['fullName'].disable();
    this.enquiryForm.controls['emailOrMobile'].disable();
  }

  public onCloseHelpPopup() {
    this.helpPopupOpen = false;
  }

  public onOpenHelpPopup() {
    this.helpPopupOpen = true;
  }

  public sendEnquiry() {
    if (this.enquiryForm.valid) {
      this.showLoader = true;
      const data = { ...this.enquiryForm.getRawValue() };
      data.isVerified = this.userSignedIn;

      this.enquiryService.sendEnquiryEmail(data).subscribe(
        (_response) => {
          this.showLoader = false;
          this.enquirySent = true;
          this.successfulResponse = true;
          this.scrollResponseIntoView();
        },
        (_error) => {
          this.successfulResponse = false;
          this.scrollResponseIntoView();
        }
      );
    }
  }

  public scrollResponseIntoView() {
    setTimeout(() => {
      this.router.navigate([], { fragment: this.responseFragment });
    }, 0);
  }

  public removeCountryCodeFromNumber(number: string): string {
    const phoneNumber = parsePhoneNumber(number, this.countryCodeISO);
    return phoneNumber.formatNational();
  }

}
